import Swiper from "swiper";
import { Autoplay, A11y } from "swiper/modules";

const DELAY = 5000;

function initFeatureSliders() {
	document.querySelectorAll("[data-feature-slider]").forEach((root) => {
		if (root.dataset.sliderInit) return;
		root.dataset.sliderInit = "true";

		const el = root.querySelector(".swiper");
		if (!el) return;

		const segments = [...root.querySelectorAll(".feature-slider__seg")];
		const labels = [...root.querySelectorAll(".feature-slider__label")];
		const toggle = root.querySelector(".feature-slider__toggle");

		const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

		/* How long the fill waits before it starts moving. Its own value in
		   style.css (`--fill-delay` on the progress bar) rather than being read
		   off the segment's transition — the head start and the resize used to be
		   the same number, and are now tuned independently.
		 *
		   Read rather than hard-coded so there is one place to change it, and so
		   the reduced-motion block zeroing it is honoured without a branch here. */
		const toMs = (value) => {
			const n = parseFloat(value);
			if (!n) return 0;
			return /ms\s*$/.test(String(value).trim()) ? n : n * 1000;
		};

		const holdMs = segments[0]
			? toMs(getComputedStyle(segments[0]).getPropertyValue("--fill-delay"))
			: 0;

		/* Which segment is filled, which is filling, which is empty — and which
		   label reads as current. Everything visual keys off the real slide index,
		   never off the DOM order of the slides, because loop mode clones them. */
		const paint = (index) => {
			segments.forEach((seg, i) => {
				seg.classList.toggle("is-done", i < index);
				seg.classList.toggle("is-active", i === index);
				if (i !== index) seg.style.removeProperty("--fill");
			});

			labels.forEach((label, i) => {
				if (i === index) label.setAttribute("aria-current", "true");
				else label.removeAttribute("aria-current");
			});
		};

		/* One state, one meaning: the timer is not running. Reduced motion starts
		   here, a label click lands here, and the button moves in and out of it.
		   The active segment reads as full whenever we are in it — there is no
		   pending advance for a part-filled bar to be counting down to. */
		const setStopped = (stopped) => {
			root.classList.toggle("is-stopped", stopped);
			toggle?.setAttribute(
				"aria-label",
				stopped ? "Start the automatic slideshow" : "Stop the automatic slideshow",
			);

			/* ⚠ Keep the live region in step with whether the timer is running.
			   Swiper decides this ONCE, at construction — `aria-live` is set to
			   "off" if autoplay is enabled and "polite" if not — so it is wrong
			   the moment either of the two things that change that happens: a
			   label click stops the rotation (changes should now be announced,
			   because the visitor asked for them), or the button restarts it
			   (they should not, or a screen reader narrates a slide every five
			   seconds indefinitely). */
			root
				.querySelector(".swiper-wrapper")
				?.setAttribute("aria-live", stopped ? "polite" : "off");
		};

		const swiper = new Swiper(el, {
			modules: [Autoplay, A11y],
			slidesPerView: 1,
			loop: true,
			/* Speed 0 is an instant swap. Under reduced motion a label click still
			   changes the image — that is the visitor asking for it — it just
			   doesn't slide there. */
			speed: reduce ? 0 : 500,
			allowTouchMove: false,
			simulateTouch: false,
			autoplay: {
				enabled: !reduce,
				delay: DELAY,
				disableOnInteraction: false,
			},
			a11y: {
				enabled: true,
				slideLabelMessage: "{{index}} of {{slidesLength}}",
			},
			on: {
				slideChange(instance) {
					paint(instance.realIndex);
				},
				/* Fires every frame while the timer runs.
				 *
				 * The fill is held at zero until the segment has finished widening,
				 * then covers the whole bar across whatever time is left. So it
				 * still hits empty and full at exactly the moments the interval
				 * starts and ends — the slide is not delayed and the interval is
				 * still 5s — it simply doesn't race the width transition it is
				 * drawn inside. Squeezing the sweep into the remaining time is what
				 * keeps both endpoints honest; delaying the sweep instead would
				 * leave it short when the slide changed.
				 *
				 * Driven off `timeLeft` rather than `progress` so it doesn't depend
				 * on which direction Swiper counts. */
				autoplayTimeLeft(instance, timeLeft) {
					const seg = segments[instance.realIndex];
					if (!seg) return;

					const span = DELAY - holdMs;
					const elapsed = DELAY - timeLeft - holdMs;
					const filled = span > 0 ? Math.min(1, Math.max(0, elapsed / span)) : 1;

					seg.style.setProperty("--fill", (filled * 100).toFixed(2) + "%");
				},
			},
		});

		paint(swiper.realIndex);
		setStopped(reduce);

		/* Picking a label stops the rotation for good. Showing someone the slide
		   they asked for and then pulling it away five seconds later is the whole
		   reason carousels are disliked; it also gives every visitor a real
		   stop mechanism without needing to find the corner button. */
		labels.forEach((label, i) => {
			label.addEventListener("click", () => {
				swiper.slideToLoop(i);
				swiper.autoplay?.stop();
				setStopped(true);
			});
		});

		toggle?.addEventListener("click", () => {
			if (root.classList.contains("is-stopped")) {
				/* ⚠ Set the param before starting. Under reduced motion autoplay
				   was constructed with `enabled: false`, and start() will not hold
				   without flipping it — the button would appear to do nothing. */
				if (swiper.params.autoplay) swiper.params.autoplay.enabled = true;
				swiper.autoplay?.start();
				setStopped(false);
			} else {
				swiper.autoplay?.stop();
				setStopped(true);
			}
		});

		// Reveals the toggle. Until now it was a button that could not work.
		root.classList.add("is-ready");
	});
}

if (document.readyState !== "loading") {
	initFeatureSliders();
} else {
	document.addEventListener("DOMContentLoaded", initFeatureSliders);
}
